import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { trainingGroupsAPI, crewsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getUserTrainingGroups, isCoach } from '../../utils/roles';
import { XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';

const TrainingSessionsFilters = ({ filters, onFiltersChange, onClearFilters }) => {
  const { user } = useAuth();
  const [localFilters, setLocalFilters] = useState(filters);

  // Get training groups data
  const { data: trainingGroups = [] } = useQuery(
    'trainingGroups',
    () => trainingGroupsAPI.getGroups(true) // Include crews
  );

  // Get crews data
  const { data: crews = [] } = useQuery('crews', () => crewsAPI.getCrews());

  // Get user's training group memberships
  const userTrainingGroups = getUserTrainingGroups(user);
  const userIsCoach = isCoach(user);

  // Get user's crew memberships (handle case where it might not be available)
  const userCrewMemberships = user?.crew_memberships || [];

  // Apply filters when local filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onFiltersChange(localFilters);
    }, 300); // Debounce filter changes

    return () => clearTimeout(timeoutId);
  }, [localFilters, onFiltersChange]);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      crewId: null,
      trainingGroupId: null,
      userParticipation: null,
      dateRange: 'upcoming',
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => value !== null && value !== 'upcoming'
  );

  // Helper function to normalize crew memberships
  const normalizeCrewMemberships = (memberships) => {
    console.log('=== CREW MEMBERSHIPS DEBUG ===');
    console.log('Raw memberships:', memberships);

    const normalized = [];
    if (memberships) {
      memberships.forEach((membership) => {
        console.log('Processing membership:', membership);
        if (typeof membership === 'object') {
          if (membership.crew_id) {
            const normalizedMembership = {
              crew_id: parseInt(membership.crew_id),
              crew: membership.crew ? { id: parseInt(membership.crew.id) } : null,
            };
            normalized.push(normalizedMembership);
            console.log('Added normalized membership (crew_id):', normalizedMembership);
          } else if (membership.crew?.id) {
            const normalizedMembership = {
              crew_id: parseInt(membership.crew.id),
              crew: { id: parseInt(membership.crew.id) },
            };
            normalized.push(normalizedMembership);
            console.log('Added normalized membership (crew.id):', normalizedMembership);
          } else {
            console.log('Skipped membership (no crew_id or crew.id):', membership);
          }
        } else {
          console.log('Skipped membership (not an object):', membership);
        }
      });
    }
    console.log('Final normalized crew memberships:', normalized);
    console.log('=== END CREW MEMBERSHIPS DEBUG ===');
    return normalized;
  };

  // Helper function to normalize training group memberships
  const normalizeTrainingGroupMemberships = (memberships) => {
    console.log('=== TRAINING GROUP MEMBERSHIPS DEBUG ===');
    console.log('Raw training group memberships:', memberships);

    const normalized = [];
    if (memberships) {
      memberships.forEach((membership) => {
        console.log('Processing training group membership:', membership);
        if (typeof membership === 'object' && membership.training_group_id) {
          const normalizedMembership = {
            training_group_id: parseInt(membership.training_group_id),
            role_in_group: membership.role_in_group,
          };
          normalized.push(normalizedMembership);
          console.log('Added normalized training group membership:', normalizedMembership);
        } else {
          console.log('Skipped training group membership (invalid format):', membership);
        }
      });
    }
    console.log('Final normalized training group memberships:', normalized);
    console.log('=== END TRAINING GROUP MEMBERSHIPS DEBUG ===');
    return normalized;
  };

  // Get available crews for the user
  const getAvailableCrews = () => {
    if (user?.is_admin) {
      return crews;
    }

    // For coaches, show crews from their training groups
    if (userIsCoach) {
      const coachTrainingGroupIds = userTrainingGroups
        .filter((membership) => membership.role_in_group === 'coach')
        .map((membership) => membership.training_group_id);

      return crews.filter(
        (crew) => crew.training_group_id && coachTrainingGroupIds.includes(crew.training_group_id)
      );
    }

    // For rowers, show crews they're members of
    const normalizedCrewMemberships = normalizeCrewMemberships(userCrewMemberships);
    return crews.filter((crew) =>
      normalizedCrewMemberships.some(
        (membership) => parseInt(membership.crew_id) === parseInt(crew.id)
      )
    );
  };

  // Get available training groups for the user
  const getAvailableTrainingGroups = () => {
    if (user?.is_admin) {
      return trainingGroups;
    }

    // For coaches, show training groups they coach
    if (userIsCoach) {
      return trainingGroups.filter((group) =>
        userTrainingGroups.some(
          (membership) =>
            parseInt(membership.training_group_id) === parseInt(group.id) &&
            membership.role_in_group === 'coach'
        )
      );
    }

    // For rowers, show training groups they're members of
    return trainingGroups.filter((group) =>
      userTrainingGroups.some(
        (membership) => parseInt(membership.training_group_id) === parseInt(group.id)
      )
    );
  };

  const availableCrews = getAvailableCrews();
  const availableTrainingGroups = getAvailableTrainingGroups();

  // Debug user data and available options
  console.log('=== USER DATA DEBUG ===');
  console.log('User:', user);
  console.log('User crew memberships:', userCrewMemberships);
  console.log('User training groups:', userTrainingGroups);
  console.log('User is coach:', userIsCoach);
  console.log('Available crews:', availableCrews);
  console.log('Available training groups:', availableTrainingGroups);
  console.log('=== END USER DATA DEBUG ===');

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          {hasActiveFilters && (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
              Active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center space-x-1 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Clear all filters</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Crew Filter */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Crew</label>
          <select
            value={localFilters.crewId || ''}
            onChange={(e) => handleFilterChange('crewId', e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All crews</option>
            {availableCrews.map((crew) => (
              <option key={crew.id} value={crew.id}>
                {crew.name}
              </option>
            ))}
          </select>
        </div>

        {/* Training Group Filter */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Training Group</label>
          <select
            value={localFilters.trainingGroupId || ''}
            onChange={(e) => handleFilterChange('trainingGroupId', e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All training groups</option>
            {availableTrainingGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>

        {/* User Participation Filter */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">My Participation</label>
          <select
            value={localFilters.userParticipation || ''}
            onChange={(e) => handleFilterChange('userParticipation', e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All sessions</option>
            <option value="assigned">Sessions I'm assigned to</option>
            <option value="crew">Sessions with my crews</option>
            <option value="training_group">Sessions in my training groups</option>
          </select>
        </div>

        {/* Date Range Filter */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Date Range</label>
          <select
            value={localFilters.dateRange || 'upcoming'}
            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="upcoming">Upcoming & today</option>
            <option value="past">Past sessions</option>
            <option value="all">All sessions</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default TrainingSessionsFilters;
